import { useCallback } from 'react';

import { useWatch, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { WellReg } from '@britishcouncil/react-registration-platform';

import { ClearForm } from '@/components/component-wrapper/ClearForm';
import { FormSelect } from '@/components/forms/FormSelect';
import { getFormError, getServerError } from '@/core/errors/FormError';
import { CountryRiskNotice } from '@/pages/book-test/components/CountryRiskNotice';
import { useCountryGetAllQuery } from '@/store/webApi';

import { PersonalDetailsFormType } from '../hooks/usePersonalDetailsForm';
import { FormInputWrapper } from '@/components/forms/FormInputWrapper';

export const AddressDetails = () => {
  const { t } = useTranslation('');

  const {
    register,
    formState: { errors },
    setValue,
    control,
  } = useFormContext<PersonalDetailsFormType>();

  const { data: countries = [], error: countriesError, isLoading: isLoadingCountries } = useCountryGetAllQuery();

  const options = countries.map((country) => ({
    value: country.id ?? -1,
    label: country.name ?? '',
  }));

  const watchCountry = useWatch({ control, name: "address.residenceCountryId" });
  const selectedCountryIsoCode = countries.find((item) => item.id === watchCountry)?.isoCode;

  const onFormClear = useCallback(() => {
    setValue('address.addressLine1', '');
    setValue('address.addressLine2', '');
    setValue('address.city', '');
    setValue('address.postCode', '');
    setValue('address.residenceCountryId', 0);
  }, [setValue]);

  return (
    <ClearForm onClear={onFormClear}>
      <WellReg>
        <CountryRiskNotice isoCode={selectedCountryIsoCode} />
        <div className="[&>*]:my-3">
          <FormSelect
            data-testid="country-of-residence"
            name={'address.residenceCountryId'}
            control={control}
            required
            options={options}
            placeholder={t('personal-details.contact-details.search-by-country')}
            label={t('personal-details.contact-details.country-of-residence')}
            errorSubmittion={typeof countriesError === 'string' ? countriesError : getServerError(errors.address?.residenceCountryId)}
            errorInline={getFormError(errors.address?.residenceCountryId)}
            disabled={isLoadingCountries}
          />
          <FormInputWrapper
            required
            data-testid="postal-address-one"
            {...register('address.addressLine1', { required: t('common.required') })}
            label={t('personal-details.contact-details.postal-address')}
          />
          <FormInputWrapper data-testid="postal-address-two" {...register('address.addressLine2')} />
          <FormInputWrapper
            required
            data-testid="town-city"
            {...register('address.city', { required: t('common.required') })}
            label={t('personal-details.contact-details.town-city')}
          />
          <FormInputWrapper
            required
            data-testid="zip-code"
            {...register('address.postCode', { required: t('common.required') })}
            label={t('personal-details.contact-details.zip-code')}
          />
        </div>
      </WellReg>
    </ClearForm>
  );
};
